import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Pause, Pencil, Play, Plus } from 'lucide-react'
import type { AppInfo, AutopilotRec, CampaignRow } from '@/api/client'
import {
  useApps,
  useAutopilot,
  useCampaigns,
  useCreateCampaign,
  useFx,
  useGoogleStatus,
  useBudgetPools,
  useMapCampaign,
  useSetCampaignPool,
  useSetCampaignBudget,
  useSetCampaignStatus,
  useSyncRuns,
} from '@/lib/queries'
import { count, localBudgetPreview, localMoney, usd } from '@/lib/money'
import { adsError, appleError, spendShown, spendState, spendHint, type SpendState } from '@/lib/spend'
import { statusOf } from '@/lib/status'
import { AppleRoasSection } from './AppleRoas'
import { BudgetPoolsSection } from './BudgetPools'
import { WriteLogSection } from './WriteLog'
import {
  Button,
  ConfirmDialog,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Input,
  IconButton,
  Money,
  Notice,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
  StatusPill,
  When,
  errorText,
  useIsWide,
  useToast,
  type Column,
} from '@/ui'

// ── Budget in dollars, with the account currency beside it ───────────────

/** The budget as Google holds it ("₩5,000"), for an account that is not in
 *  USD; "" otherwise. */
function budgetLocal(c: CampaignRow): string {
  return c.currency && c.currency !== 'USD' && (c.daily_budget_local ?? 0) > 0 ? localMoney(c.daily_budget_local, c.currency) : ''
}

/** "상한 $9.00 중 $3.20", or that the sync paused it there; "" without a cap. */
function capLine(c: CampaignRow): string {
  if (!((c.spend_cap_usd ?? 0) > 0)) return ''
  if (c.capped_at) return `상한 ${usd(c.spend_cap_usd)} 도달 · 일시정지함`
  return `상한 ${usd(c.spend_cap_usd)} 중 ${usd(c.spend_total_usd ?? 0)}`
}

/** What a recommendation shows and applies, in dollars. A response from before
 *  the conversion carried only micros, which were then dollars. */
const recNewUSD = (r: AutopilotRec) => r.new_budget_usd ?? r.new_budget_micros / 1e6
const recCurrentUSD = (r: AutopilotRec) => r.current_budget_usd ?? r.current_budget_micros / 1e6

/** "일 예산 $5.00로 바꿨다 (₩6,628)": what Google actually received. */
function localSuffix(res: { currency?: string; amount_local?: number } | undefined): string {
  return res?.currency && res.currency !== 'USD' && res.amount_local ? ` (${localMoney(res.amount_local, res.currency)})` : ''
}

// ── Spend state notice ──────────────────────────────────────────────────────

/** Google Ads' advertising channel as the Ads UI names it in Korean. An
 *  enum the map does not know shows as it came (still better than nothing). */
const CHANNEL: Record<string, string> = {
  MULTI_CHANNEL: '앱 캠페인',
  SEARCH: '검색',
  DISPLAY: '디스플레이',
  VIDEO: '동영상',
  SHOPPING: '쇼핑',
  PERFORMANCE_MAX: '실적 최대화',
  DEMAND_GEN: '디맨드젠',
  SMART: '스마트',
  APPSTORE_APP: 'App Store',
}
const channelLabel = (t: string) => (t ? (CHANNEL[t] ?? t) : '—')

function SpendNotice({ state, runs }: { state: SpendState; runs: Parameters<typeof adsError>[0] }) {
  if (state === 'ok') return null
  const link = (
    <Link to="/settings/google" className="font-medium text-foreground underline underline-offset-2">
      Google 연결
    </Link>
  )
  if (state === 'unlinked') {
    return (
      <Notice tone="neutral" title="Google Ads 미연결">
        캠페인과 지출이 들어오지 않는다. {link}에서 Ads를 연결한다.
      </Notice>
    )
  }
  if (state === 'blocked' || state === 'error') {
    const err = adsError(runs)
    return (
      <Notice
        tone="failed"
        title={state === 'blocked' ? 'Google Ads 접근 거부 — 지출이 들어오지 않는다' : 'Google Ads 조회 오류 — 지출이 들어오지 않는다'}
        detail={err?.message}
      >
        {err?.at && (
          <>
            마지막 시도 <When iso={err.at} mode="absolute" />.{' '}
          </>
        )}
        {state === 'blocked' ? '승인이 나기 전까지' : '다음 동기화가 성공하기 전까지'} 지출 칸은 $0.00이 아니라 —로 둔다. {link}
      </Notice>
    )
  }
  return (
    <Notice tone="neutral" title="캠페인 없음">
      Google Ads는 연결됐지만 가져온 캠페인이 없다. 새 캠페인을 만들거나 동기화를 기다린다.
    </Notice>
  )
}

/** Apple Ads came in with an error on the last pass. Google's own state is
 *  told apart above; this one never hides the Apple rows already stored. */
function AppleNotice({ runs }: { runs: Parameters<typeof appleError>[0] }) {
  const err = appleError(runs)
  if (!err) return null
  return (
    <Notice tone="failed" title="Apple Ads 조회 오류 — Apple 지출이 새로 들어오지 않는다" detail={err.message}>
      {err.at && (
        <>
          마지막 시도 <When iso={err.at} mode="absolute" />.{' '}
        </>
      )}
      저장된 지출로 묶음 정지는 계속 돈다. 다음 동기화가 성공하면 풀린다.{' '}
      <Link to="/settings" className="font-medium text-foreground underline underline-offset-2">
        설정
      </Link>
    </Notice>
  )
}

// ── Recommendations ─────────────────────────────────────────────────────────

type Pending =
  | { kind: 'rec'; rec: AutopilotRec }
  | { kind: 'status'; row: CampaignRow }
  | null

function Recommendations({ onApply }: { onApply: (rec: AutopilotRec) => void }) {
  const recs = useAutopilot()
  const rows = recs.data ?? []
  return (
    <section aria-label="권고" className="rounded-lg border border-border bg-card">
      <header className="flex flex-wrap items-baseline justify-between gap-x-3 border-b border-border px-4 py-3">
        <h2 className="text-section font-semibold">권고</h2>
        <span className="text-meta text-muted-foreground">최근 7일 기준 · 적용은 확인 뒤 광고 플랫폼에 바로 반영된다</span>
      </header>
      {recs.isLoading ? (
        <div className="p-4">
          <Skeleton className="h-4 w-64 max-w-full" />
        </div>
      ) : recs.error ? (
        <p className="px-4 py-3 font-mono text-meta text-status-failed">{errorText(recs.error)}</p>
      ) : rows.length === 0 ? (
        <p className="px-4 py-3 text-table text-muted-foreground">권고 없음 — 7일 지출 $10 이상인 운영 중 캠페인이 규칙 안이다.</p>
      ) : (
        <ul className="divide-y divide-border">
          {rows.map((r) => (
            <li key={r.campaign_id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
              <div className="min-w-48 flex-1">
                <div className="text-body font-medium">{r.campaign_name}</div>
                <div className="text-meta text-muted-foreground">
                  {r.app_name ? `${r.app_name} · ` : ''}
                  {statusOf('autopilotReason', r.reason).label}
                </div>
              </div>
              <div className="text-table tabular-nums text-muted-foreground">
                7일 지출 <Money value={r.spend_7d} className="text-foreground" /> · ROAS{' '}
                <span className="text-foreground">{r.roas_7d.toFixed(2)}×</span>
              </div>
              <Button size="sm" onClick={() => onApply(r)}>
                {r.action === 'pause' ? '일시정지' : `예산 +20% → ${usd(recNewUSD(r))}`}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

// ── New campaign ────────────────────────────────────────────────────────────

/** The lines of a textarea, trimmed, empty ones dropped. */
const lines = (t: string) => t.split('\n').map((l) => l.trim()).filter(Boolean)

/** "2/5줄 · 가장 긴 줄 25/30자", or what is wrong with the lines. */
function linesNote(t: string, max: number): { note: string; ok: boolean } {
  const ls = lines(t)
  const longest = ls.reduce((n, l) => Math.max(n, [...l].length), 0)
  const ok = ls.length >= 1 && ls.length <= 5 && longest <= max
  return { note: `${ls.length}/5줄 · 가장 긴 줄 ${longest}/${max}자`, ok }
}

function NewCampaignDialog({ open, onClose, apps }: { open: boolean; onClose: () => void; apps: AppInfo[] }) {
  const create = useCreateCampaign()
  const fx = useFx()
  const toast = useToast()
  const [name, setName] = useState('')
  const [appId, setAppId] = useState('')
  const [storeAppId, setStoreAppId] = useState('')
  const [amount, setAmount] = useState('')
  const [countries, setCountries] = useState('US, CA, GB, AU')
  const [languages, setLanguages] = useState('en')
  const [headlines, setHeadlines] = useState('')
  const [descriptions, setDescriptions] = useState('')
  const [endDate, setEndDate] = useState('')
  const [cap, setCap] = useState('')
  const [err, setErr] = useState<string | null>(null)

  const codes = (t: string) => t.split(/[\s,]+/).map((c) => c.trim()).filter(Boolean)
  const head = linesNote(headlines, 30)
  const desc = linesNote(descriptions, 90)
  const capN = cap.trim() === '' ? 0 : Number(cap)
  const capOK = capN === 0 || capN >= Number(amount)
  const valid =
    name.trim() !== '' && storeAppId.trim() !== '' && Number(amount) >= 1 && codes(countries).length > 0 && head.ok && desc.ok && capOK

  const close = () => {
    setErr(null)
    onClose()
  }

  const body = (validateOnly: boolean) => ({
    name,
    store_app_id: storeAppId,
    app_id: appId ? Number(appId) : undefined,
    amount_usd: Number(amount),
    campaign_type: 'APP_INSTALLS' as const,
    countries: codes(countries).map((c) => c.toUpperCase()),
    languages: codes(languages).map((c) => c.toLowerCase()),
    headlines: lines(headlines),
    descriptions: lines(descriptions),
    end_date: endDate || undefined,
    spend_cap_usd: capN || undefined,
    validate_only: validateOnly || undefined,
  })

  async function check() {
    setErr(null)
    try {
      await create.mutateAsync(body(true))
      toast.success('Google Ads가 요청을 받아들였다. 아직 만들지 않았다')
    } catch (e) {
      setErr(errorText(e))
    }
  }

  async function submit() {
    setErr(null)
    try {
      const res = await create.mutateAsync(body(false))
      toast.success(`캠페인 "${name}"을 일시정지 상태로 만들었다${localSuffix(res)}`)
      setName('')
      setStoreAppId('')
      setAmount('')
      setAppId('')
      setHeadlines('')
      setDescriptions('')
      setEndDate('')
      setCap('')
      onClose()
    } catch (e) {
      setErr(errorText(e))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      title="새 캠페인"
      description="앱 설치 캠페인을 Google Ads에 일시정지 상태로 만든다. 검토한 뒤 목록에서 운영으로 바꾼다."
      footer={
        <>
          <Button onClick={close}>취소</Button>
          <Button disabled={!valid || create.isPending} onClick={check}>
            검증
          </Button>
          <Button variant="primary" disabled={!valid} loading={create.isPending} onClick={submit}>
            만들기
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="캠페인 이름" required>
          <Input placeholder="Lotto Lens installs" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="앱 연결" hint="고르면 Play 패키지가 자동으로 채워진다. 비워도 된다.">
          <Select
            value={appId}
            onChange={(e) => {
              setAppId(e.target.value)
              const a = apps.find((x) => x.id === Number(e.target.value))
              if (a?.package_name) setStoreAppId(a.package_name)
            }}
          >
            <option value="">연결 안 함</option>
            {apps.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Play 패키지" required>
          <Input className="font-mono" placeholder="com.pentarab.lottolens" value={storeAppId} onChange={(e) => setStoreAppId(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="일 예산 ($)" required hint={localBudgetPreview(Number(amount), fx.data) || '1 이상'}>
            <Input inputMode="decimal" placeholder="5" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <Field
            label="총 지출 상한 ($)"
            error={capOK ? undefined : '일 예산 이상이어야 한다'}
            hint="닿으면 다음 동기화 때 일시정지. Google 보고가 몇 시간 늦어 조금 넘을 수 있다."
          >
            <Input inputMode="decimal" placeholder="없음" value={cap} onChange={(e) => setCap(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="국가" required hint="ISO 코드, 쉼표로 구분">
            <Input className="font-mono" value={countries} onChange={(e) => setCountries(e.target.value)} />
          </Field>
          <Field label="언어" hint="비우면 모든 언어">
            <Input className="font-mono" value={languages} onChange={(e) => setLanguages(e.target.value)} />
          </Field>
        </div>
        <Field label="광고 제목" required hint={`한 줄에 하나 · ${head.note}`} error={headlines && !head.ok ? head.note : undefined}>
          <Textarea rows={4} placeholder="Printable Graph Paper PDF" value={headlines} onChange={(e) => setHeadlines(e.target.value)} />
        </Field>
        <Field label="광고 설명" required hint={`한 줄에 하나 · ${desc.note}`} error={descriptions && !desc.ok ? desc.note : undefined}>
          <Textarea rows={4} placeholder="Works offline. No account, no sign-up." value={descriptions} onChange={(e) => setDescriptions(e.target.value)} />
        </Field>
        <Field label="종료일" hint="이날까지 게재한다. 비우면 계속">
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </Field>
        {err && <p role="alert" className="whitespace-pre-wrap break-words font-mono text-meta text-status-failed">{err}</p>}
      </div>
    </Dialog>
  )
}

// ── Budget ──────────────────────────────────────────────────────────────────

function BudgetDialog({ row, onClose, onSave, pending }: { row: CampaignRow | null; onClose: () => void; onSave: (amount: number) => void; pending: boolean }) {
  const fx = useFx()
  const [text, setText] = useState('')
  const [seen, setSeen] = useState<number | null>(null)
  // Start empty whenever a different campaign opens.
  if (row && seen !== row.id) {
    setSeen(row.id)
    setText('')
  }
  if (!row && seen !== null) setSeen(null)
  const n = Number(text)
  const valid = text.trim() !== '' && Number.isFinite(n) && n > 0
  const current = row?.daily_budget_usd ?? 0
  return (
    <Dialog
      open={!!row}
      onClose={onClose}
      size="sm"
      title="일 예산 바꾸기"
      description={
        row
          ? `${row.name} · 현재 ${current > 0 ? `${usd(current)} / 일${budgetLocal(row) ? ` (${budgetLocal(row)})` : ''}` : budgetLocal(row) ? `${budgetLocal(row)} / 일` : '예산을 아직 읽지 못했다'}. 저장하면 광고 플랫폼에 바로 반영된다.`
          : undefined
      }
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button variant="primary" disabled={!valid} loading={pending} onClick={() => valid && onSave(n)}>
            저장
          </Button>
        </>
      }
    >
      <Field label="새 일 예산 ($)" hint={row?.network === 'apple' ? '달러 그대로 보낸다 (센트 단위로 반올림)' : localBudgetPreview(n, fx.data) || undefined}>
        <Input inputMode="decimal" placeholder={current > 0 ? String(current) : 'USD'} value={text} onChange={(e) => setText(e.target.value)} />
      </Field>
    </Dialog>
  )
}

// ── Page ────────────────────────────────────────────────────────────────────

export default function CampaignsPage() {
  const camps = useCampaigns(30)
  const apps = useApps()
  const google = useGoogleStatus()
  const sync = useSyncRuns()
  const toast = useToast()
  const mapApp = useMapCampaign()
  const setStatus = useSetCampaignStatus()
  const setBudget = useSetCampaignBudget()
  const setPool = useSetCampaignPool()
  const pools = useBudgetPools()
  // Eleven columns do not fit beside the sidebar under 1024px: cards there.
  const wide = useIsWide()

  const [creating, setCreating] = useState(false)
  const [pending, setPending] = useState<Pending>(null)
  const [budgetRow, setBudgetRow] = useState<CampaignRow | null>(null)
  const [rowErr, setRowErr] = useState<Record<number, string>>({})

  const ready = !google.isLoading && !sync.isLoading
  const state = spendState({ status: google.data, runs: sync.runs })
  const shown = ready && spendShown(state)
  // A Google failure says nothing about Apple's figures.
  const costShown = (c: CampaignRow) => shown || (ready && c.network === 'apple')
  const hint = spendHint(state)
  // Settings › Google Ads / AdMob switched off (accounts closed 2026-11): no
  // Google write is offered, and with no AdMob revenue an app mapping feeds no
  // ROAS, so the unmapped warning has nothing to say.
  const googleOff = google.data?.ads_enabled === false
  const admobOff = google.data?.admob_enabled === false

  const rows = camps.data
  const unmapped = (rows ?? []).filter((c) => !c.app_id)
  const unmappedCost = unmapped.reduce((s, c) => s + c.cost, 0)
  const totalCost = (rows ?? []).reduce((s, c) => s + c.cost, 0)

  const fail = (id: number | null, what: string, e: unknown) => {
    toast.error(`${what}: ${errorText(e)}`)
    if (id != null) setRowErr((m) => ({ ...m, [id]: errorText(e) }))
  }
  const clear = (id: number) =>
    setRowErr((m) => {
      if (!(id in m)) return m
      const next = { ...m }
      delete next[id]
      return next
    })

  async function applyPending() {
    if (!pending) return
    try {
      if (pending.kind === 'rec') {
        const r = pending.rec
        if (r.action === 'pause') {
          await setStatus.mutateAsync({ id: r.campaign_id, status: 'PAUSED' })
          toast.success(`"${r.campaign_name}"을 일시정지했다`)
        } else {
          const res = await setBudget.mutateAsync({ id: r.campaign_id, amount: recNewUSD(r) })
          toast.success(`"${r.campaign_name}" 일 예산을 ${usd(recNewUSD(r))}로 올렸다${localSuffix(res)}`)
        }
        clear(r.campaign_id)
      } else {
        const next = pending.row.status === 'ENABLED' ? 'PAUSED' : 'ENABLED'
        await setStatus.mutateAsync({ id: pending.row.id, status: next })
        toast.success(next === 'PAUSED' ? `"${pending.row.name}"을 일시정지했다` : `"${pending.row.name}"을 다시 운영한다`)
        clear(pending.row.id)
      }
    } catch (e) {
      fail(pending.kind === 'rec' ? pending.rec.campaign_id : pending.row.id, '반영하지 못했다', e)
    }
    setPending(null)
  }

  async function saveBudget(amount: number) {
    if (!budgetRow) return
    try {
      const res = await setBudget.mutateAsync({ id: budgetRow.id, amount })
      toast.success(`"${budgetRow.name}" 일 예산을 ${usd(amount)}로 바꿨다${localSuffix(res)}`)
      clear(budgetRow.id)
    } catch (e) {
      fail(budgetRow.id, '예산을 바꾸지 못했다', e)
    }
    setBudgetRow(null)
  }

  async function changeApp(row: CampaignRow, value: string) {
    try {
      await mapApp.mutateAsync({ id: row.id, appId: value ? Number(value) : null })
      toast.success(value ? `"${row.name}"을 앱에 연결했다` : `"${row.name}"의 앱 연결을 풀었다`)
      clear(row.id)
    } catch (e) {
      fail(row.id, '앱 연결을 바꾸지 못했다', e)
    }
  }

  async function changePool(row: CampaignRow, value: string) {
    try {
      await setPool.mutateAsync({ id: row.id, poolId: value ? Number(value) : null })
      const name = pools.data?.find((p) => p.id === Number(value))?.name
      toast.success(value ? `"${row.name}"을 묶음 "${name ?? ''}"에 넣었다` : `"${row.name}"을 묶음에서 뺐다`)
      clear(row.id)
    } catch (e) {
      fail(row.id, '묶음을 바꾸지 못했다', e)
    }
  }

  const appList = apps.data ?? []
  const poolList = pools.data ?? []

  const columns: Column<CampaignRow>[] = [
    {
      key: 'name',
      header: '캠페인',
      card: 'title',
      cell: (c) => (
        <div className="min-w-0">
          <div className="max-w-64 truncate font-medium" title={c.name}>
            {c.name}
          </div>
          <div className="text-meta text-muted-foreground">
            <span className="font-mono">{c.ads_id}</span> · <span title={c.channel_type || undefined}>{channelLabel(c.channel_type)}</span>
          </div>
          {rowErr[c.id] && <p role="alert" className="mt-0.5 whitespace-pre-wrap break-words font-mono text-meta text-status-failed">{rowErr[c.id]}</p>}
        </div>
      ),
    },
    { key: 'status', header: '상태', card: 'meta', cell: (c) => <StatusPill domain="campaign" status={c.status} /> },
    {
      key: 'app',
      header: '앱 연결',
      cell: (c) => (
        <Select
          aria-label={`${c.name} 앱 연결`}
          className="h-8 max-w-48"
          value={c.app_id ?? ''}
          onChange={(e) => changeApp(c, e.target.value)}
        >
          <option value="">연결 안 함</option>
          {appList.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </Select>
      ),
    },
    { key: 'cost', header: '지출', align: 'right', card: 'hidden', cell: (c) => <Money value={c.cost} hidden={!costShown(c)} hint={hint} /> },
    { key: 'impressions', header: '노출', align: 'right', card: 'hidden', cell: (c) => <span className="tabular-nums">{count(c.impressions)}</span> },
    { key: 'clicks', header: '클릭', align: 'right', card: 'hidden', cell: (c) => <span className="tabular-nums">{count(c.clicks)}</span> },
    { key: 'conv', header: '전환', align: 'right', card: 'hidden', cell: (c) => <span className="tabular-nums">{count(c.conversions)}</span> },
    {
      key: 'cpa',
      header: 'CPA',
      align: 'right',
      card: 'hidden',
      cell: (c) => (c.conversions > 0 ? <Money value={c.cpa} hidden={!costShown(c)} hint={hint} /> : <span className="text-muted-foreground">—</span>),
    },
    {
      key: 'budget',
      header: '일 예산',
      align: 'right',
      card: 'hidden',
      cell: (c) =>
        (c.daily_budget_usd ?? 0) > 0 ? (
          <span className="inline-flex flex-col items-end">
            <Money value={c.daily_budget_usd} />
            {budgetLocal(c) && <span className="text-meta text-muted-foreground">{budgetLocal(c)}</span>}
            {capLine(c) && <span className="text-meta text-muted-foreground">{capLine(c)}</span>}
          </span>
        ) : budgetLocal(c) ? (
          <span className="text-meta text-muted-foreground">{budgetLocal(c)}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'pool',
      header: '묶음',
      card: 'line',
      cell: (c) => (
        <Select
          aria-label={`${c.name} 예산 묶음`}
          className="h-8 max-w-52"
          value={c.pool_id ?? ''}
          onChange={(e) => changePool(c, e.target.value)}
        >
          <option value="">묶음 없음</option>
          {poolList.map((p) => (
            <option key={p.id} value={p.id} disabled={p.status !== 'ACTIVE' && p.id !== c.pool_id}>
              {p.name}
              {p.status === 'ACTIVE' ? '' : ` (${statusOf('pool', p.status).label})`}
            </option>
          ))}
        </Select>
      ),
    },
    {
      key: 'network',
      header: '네트워크',
      card: 'line',
      cell: (c) => <StatusPill domain="network" status={c.network ?? 'google'} />,
    },
    // The phone's card: the figures on two short lines instead of five label
    // rows (a card ran to nine lines).
    {
      key: 'figures',
      header: '지출 · 노출 · 클릭 · 전환',
      cardOnly: true,
      card: 'sub',
      cell: (c) => (
        <span className="block tabular-nums">
          <span className="whitespace-nowrap">
            지출 <Money value={c.cost} hidden={!costShown(c)} hint={hint} className="text-foreground" />
          </span>
          {' · '}노출 <span className="text-foreground">{count(c.impressions)}</span>
          {' · '}클릭 <span className="text-foreground">{count(c.clicks)}</span>
          {' · '}전환 <span className="text-foreground">{count(c.conversions)}</span>
          {c.conversions > 0 && costShown(c) && (
            <>
              {' · '}CPA <Money value={c.cpa} className="text-foreground" />
            </>
          )}
          <span className="block">
            일 예산 {(c.daily_budget_usd ?? 0) > 0 ? <Money value={c.daily_budget_usd} className="text-foreground" /> : '—'}
            {budgetLocal(c) && ` · ${budgetLocal(c)}`}
          </span>
        </span>
      ),
    },
    {
      key: 'act',
      header: <span className="sr-only">처리</span>,
      align: 'right',
      card: 'actions',
      cell: (c) => (
        <div className="inline-flex items-center gap-1">
          <IconButton
            icon={c.status === 'ENABLED' ? Pause : Play}
            size="sm"
            label={c.status === 'ENABLED' ? `${c.name} 일시정지` : `${c.name} 운영 재개`}
            disabled={googleOff && c.network !== 'apple'}
            onClick={() => setPending({ kind: 'status', row: c })}
          />
          <IconButton
            icon={Pencil}
            size="sm"
            label={`${c.name} 예산 바꾸기`}
            disabled={googleOff && c.network !== 'apple'}
            onClick={() => setBudgetRow(c)}
          />
        </div>
      ),
    },
  ]

  const confirmCopy = (() => {
    if (!pending) return { title: '', description: '', label: '확인' }
    if (pending.kind === 'rec') {
      const r = pending.rec
      return r.action === 'pause'
        ? { title: `"${r.campaign_name}"을 일시정지할까`, description: '광고 플랫폼에 바로 반영된다. 광고가 즉시 멈춘다.', label: '일시정지' }
        : {
            title: `"${r.campaign_name}" 예산을 올릴까`,
            description: `일 예산을 ${usd(recCurrentUSD(r))}에서 ${usd(recNewUSD(r))}로 바꾼다. 광고 플랫폼에 바로 반영된다.`,
            label: '예산 올리기',
          }
    }
    const r = pending.row
    return r.status === 'ENABLED'
      ? { title: `"${r.name}"을 일시정지할까`, description: '광고 플랫폼에 바로 반영된다. 광고가 즉시 멈춘다.', label: '일시정지' }
      : { title: `"${r.name}"을 다시 운영할까`, description: '광고 플랫폼에 바로 반영된다. 광고가 다시 나가고 예산이 쓰이기 시작한다.', label: '운영 재개' }
  })()

  return (
    <>
      <PageHeader
        title="캠페인"
        description={
          googleOff
            ? '최근 30일 광고 지출. Google Ads는 꺼 두었다(설정 › Google Ads). Apple 캠페인의 운영과 예산 변경은 Apple Ads에 바로 반영된다.'
            : '최근 30일 광고 지출. 운영과 예산 변경은 광고 플랫폼에 바로 반영된다.'
        }
        actions={
          <Button variant="primary" icon={Plus} disabled={googleOff} onClick={() => setCreating(true)}>
            새 캠페인
          </Button>
        }
      />

      <div className="space-y-5">
        {ready && <SpendNotice state={state} runs={sync.runs} />}
        {ready && <AppleNotice runs={sync.runs} />}

        <BudgetPoolsSection campaigns={rows ?? []} />

        <AppleRoasSection runs={sync.runs} />

        <Recommendations onApply={(rec) => setPending({ kind: 'rec', rec })} />

        {!admobOff && unmapped.length > 0 && (
          <Notice tone="publishing" title={`${unmapped.length}개 캠페인이 앱에 연결되지 않았다`}>
            {unmappedCost > 0
              ? `${usd(unmappedCost)}가 앱별 ROAS에 안 들어간다. 아래 표에서 앱을 고른다.`
              : '앱을 고르면 지출이 앱별 ROAS에 들어간다. 아래 표에서 앱을 고른다.'}
          </Notice>
        )}

        <section aria-label="캠페인 지출">
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-section font-semibold">
              지출 · 최근 30일
              {rows && <span className="ml-2 text-table font-normal text-muted-foreground">{rows.length}개</span>}
            </h2>
            {rows && rows.length > 0 && (
              <span className="text-table text-muted-foreground">
                합계 <Money value={totalCost} hidden={!shown} hint={hint} className="text-foreground" />
              </span>
            )}
          </div>
          <DataTable
            label="캠페인 지출"
            columns={columns}
            rows={rows}
            rowKey={(c) => c.id}
            mode={wide ? 'table' : 'cards'}
            loading={camps.isLoading}
            error={camps.error}
            onRetry={() => camps.refetch()}
            empty={
              <EmptyState
                title="캠페인이 없다"
                description="Google Ads나 Apple Ads를 연결하고 동기화하면 캠페인이 들어온다. Google 캠페인은 새 캠페인으로 바로 만들 수도 있다."
                actions={
                  <Button variant="primary" icon={Plus} disabled={googleOff} onClick={() => setCreating(true)}>
                    새 캠페인
                  </Button>
                }
              />
            }
          />
        </section>

        <WriteLogSection />
      </div>

      <NewCampaignDialog open={creating} onClose={() => setCreating(false)} apps={appList} />
      <BudgetDialog row={budgetRow} onClose={() => setBudgetRow(null)} onSave={saveBudget} pending={setBudget.isPending} />
      <ConfirmDialog
        open={!!pending}
        onOpenChange={(o) => !o && setPending(null)}
        title={confirmCopy.title}
        description={confirmCopy.description}
        confirmLabel={confirmCopy.label}
        loading={setStatus.isPending || setBudget.isPending}
        onConfirm={applyPending}
      />
    </>
  )
}
